import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FulfillmentActionItemCode } from '@ekhodealer/ekho-common';

import type { MarkDeliveryFulfilledResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * API function to mark a vehicle delivery as fulfilled
 */
export const markDeliveryFulfilled = async (
	servicesProviderId: string,
	orderId: string,
): Promise<MarkDeliveryFulfilledResponse> => {
	const response = await apiClient.post<MarkDeliveryFulfilledResponse>(
		ordersEndpoints.markDeliveryFulfilled(servicesProviderId, orderId),
		{},
	);
	return response.data;
};

/**
 * Hook for marking a purchased vehicle (order) delivery as fulfilled
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useMarkDeliveryFulfilled = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to mark delivery as fulfilled without an order ID or services provider ID.',
				);
			}

			return markDeliveryFulfilled(servicesProviderId, orderId);
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, FulfillmentActionItemCode.MARK_DELIVERY_FULFILLED);
		},
	});
};
